import {ReactNode, useCallback, useState} from "react"
import {ChannelsContext} from "../contexts/ChannelsContext"
import {IGenre} from "../interfaces/IGenre";
import {IChannel} from "../interfaces/IChannel";
import DateHelper from "../helpers/dateHelper";
import apiNet from "../newApi/apiNet";
import {RecommendationService} from "../services/recommendationService.ts";

interface Props {
    children: ReactNode
}

const ChannelsProvider = ({children}: Props) => {
    const [channels, setChannels] = useState<IChannel[]>([]);
    const [genres, setGenres] = useState<IGenre[]>([]);
    const [filteredChannels, setFilteredChannels] = useState<IChannel[]>([]);
    const [recommendedChannels, setRecommendedChannels] = useState<IChannel[]>([]);

    // services
    const _recommendationsService = new RecommendationService();

    const updateEpgListToActualTime = (channelList: IChannel[]) => {
        channelList.forEach((x) => {
            const epgListLength = x.epgList.length;
            if (epgListLength > 0) {
                x.epgList.forEach(async (epg) => {
                    if (epg.stop_timestamp < DateHelper.GetDateTimeNowAsUnixTimespan()) {
                        if ((x.epgList.length - 1) === 0) {
                            const result = await apiNet.getNextEpgItems(x.ch_id, epg.stop_timestamp, 2);
                            x.epgList.push(...result.data);
                            x.epgList.shift();
                        } else
                            x.epgList.shift();
                    }
                })
            }
        });
        setChannels([...channelList])
    }

    const getChannels = useCallback(async () => {
        const response = await apiNet.getChannelList();
        let channels = response.data;
        channels = channels.sort((x, y) => x.position - y.position);
        setChannels(channels);
        setFilteredChannels(response.data);
        const genresArr: IGenre[] = [];
        response.data.filter(x => x.enabled).map(x => {
            if (!genresArr.find(y => y.id === x.genre_id))
                genresArr.push({id: x.genre_id, name: x.genre_name} as IGenre)
        })
        setGenres(genresArr);
    }, []);

    const addChannel = (newChannel: IChannel) => {
        setChannels((prev) => {
            if (prev.some(channel => channel.ch_id === newChannel.ch_id)) {
                return prev;
            }
            return [...prev, newChannel];
        });
    };

    const updateChannel = (channelToUpdate: IChannel) => {
        setChannels(prev =>
            prev.map(channel =>
                channel.ch_id === channelToUpdate.ch_id ? channelToUpdate : channel
            )
        );
    }

    const getRecommendedChannels = () => {
        const recommendedChannelList = _recommendationsService.getRecommendedChannelList(channels, 3);
        setRecommendedChannels(recommendedChannelList);
    }

    const setFilteredChannelsCtx = (filteredChannelsList: IChannel[]) =>
        setFilteredChannels(filteredChannelsList);
    const setRecommendedChannelsCtx = (
        recommendedChannelsList: IChannel[]
    ) => setRecommendedChannels(recommendedChannelsList);
    const setAllChannelsCtx = (channelsList: IChannel[]) =>
        setChannels(channelsList);

    return <ChannelsContext.Provider value={{
        channels,
        getChannels,
        getRecommendedChannels,
        updateEpgListToActualTime,
        allChannels: channels,
        genres,
        filteredChannels,
        recommendedChannels,
        setAllChannels: setAllChannelsCtx,
        setFilteredChannels: setFilteredChannelsCtx,
        setRecommendedChannels: setRecommendedChannelsCtx,
        addChannel,
        updateChannel
    }}>
        {children}
    </ChannelsContext.Provider>
}

export default ChannelsProvider;